import classnames from 'classnames';
import type { ComponentChildren, JSX } from 'preact';
import { useCallback, useContext, useEffect, useMemo } from 'preact/hooks';

import { useArrowKeyNavigation } from '../../hooks/use-arrow-key-navigation';
import { useStableCallback } from '../../hooks/use-stable-callback';
import { useSyncedRef } from '../../hooks/use-synced-ref';
import type { CompositeProps, Order, OrderDirection } from '../../types';
import { downcastRef } from '../../util/typing';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  OrderableIcon,
  SpinnerSpokesIcon,
} from '../icons';
import { Button } from '../input';
import ScrollContext from './ScrollContext';
import Table from './Table';
import type { TableProps } from './Table';
import TableBody from './TableBody';
import TableCell from './TableCell';
import TableFoot from './TableFoot';
import TableHead from './TableHead';
import TableRow from './TableRow';

export type TableColumn<Field> = {
  field: Field;
  label: string;
  classes?: string;
};

type ComponentProps<Row> = Pick<
  TableProps,
  'borderless' | 'title' | 'striped' | 'grid'
> & {
  rows: Row[];
  columns: TableColumn<keyof Row>[];

  /** Content to render if rows is empty (and not in a loading state) */
  emptyMessage?: ComponentChildren;
  loading?: boolean;
  selectedRow?: Row | null;

  /**
   * Callback when a row is "selected" by click or key press.
   *
   * If using multi-selection (see {@link ComponentProps.selectedRows}) this
   * will be passed only the most recently selected row. Use
   * {@link ComponentProps.onSelectRows} to receive the full selection.
   */
  onSelectRow?: (r: Row) => void;

  /**
   * Selected rows. If this property is set it enables multi-selection. The
   * user will be able to select a contiguous range of rows via shift+click or
   * shift + arrow keys.
   */
  selectedRows?: Row[];

  /**
   * Callback when rows are selected by click or key press.
   *
   * If multi-selection is enabled, this may have multiple entries (see
   * {@link ComponentProps.selectedRows} otherwise it will have one entry.
   */
  onSelectRows?: (r: Row[]) => void;

  /**
   * Callback when a row is "confirmed" by double-click or pressing "Enter"
   */
  onConfirmRow?: (r: Row) => void;

  /** Current sort order */
  order?: Order<keyof Row>;

  /**
   * Callback invoked when user clicks a column header to change the sort order.
   * When a header is clicked, if that's not the active order, it is set with
   * order='ascending'.
   * If the active header is clicked consecutively, direction toggles between
   * 'ascending' and 'descending'.
   */
  onOrderChange?: (order: Order<keyof Row>) => void;

  /**
   * Columns that can be used to order the table. Ignored if `onOrderChange` is
   * not provided.
   * No columns will be orderable if this is not provided.
   *
   * This can be a map of columns and order directions, to indicate the initial
   * direction to use when a column becomes the ordered one
   */
  orderableColumns?:
    | Array<keyof Row>
    | Partial<Record<keyof Row, OrderDirection>>;

  /** Callback to render an individual table cell */
  renderItem?: (r: Row, field: keyof Row) => ComponentChildren;
};

export type DataTableProps<Row> = CompositeProps &
  ComponentProps<Row> &
  Omit<JSX.HTMLAttributes<HTMLElement>, 'size' | 'rows' | 'role' | 'loading'>;

function defaultRenderItem<Row>(r: Row, field: keyof Row): ComponentChildren {
  return r[field] as ComponentChildren;
}

function calculateNewOrder<T extends string | number | symbol>(
  newField: T,
  prevOrder?: Order<T>,
  initialOrderForColumn?: Partial<Record<T, OrderDirection>>,
): Order<T> {
  if (newField !== prevOrder?.field) {
    return {
      field: newField,
      direction: initialOrderForColumn?.[newField] ?? 'ascending',
    };
  }

  const newDirection =
    prevOrder.direction === 'ascending' ? 'descending' : 'ascending';
  return { field: newField, direction: newDirection };
}

type HeaderComponentProps = {
  onClick?: () => void;
  children: ComponentChildren;
  field: string;
};

function HeaderComponent({ children, onClick, field }: HeaderComponentProps) {
  const commonClasses = 'flex justify-between items-center';
  return onClick ? (
    <Button
      classes={`${commonClasses} w-full h-full !p-3`}
      variant="custom"
      onClick={onClick}
      data-testid={`${field}-order-button`}
    >
      {children}
    </Button>
  ) : (
    <div className={commonClasses}>{children}</div>
  );
}

/**
 * An interactive table of rows and columns with a sticky header.
 */
export default function DataTable<Row>({
  children,
  elementRef,

  columns = [],
  rows = [],
  selectedRow,
  selectedRows,
  loading = false,
  renderItem = defaultRenderItem,
  onSelectRow,
  onSelectRows,
  onConfirmRow,
  emptyMessage,

  order,
  onOrderChange,
  orderableColumns = [],

  // Forwarded to Table
  title,
  borderless,
  striped,
  grid,

  ...htmlAttributes
}: DataTableProps<Row>) {
  const tableRef = useSyncedRef(elementRef);
  const scrollContext = useContext(ScrollContext);
  const [orderableColumnsList, initialOrderForColumn] = useMemo(
    () =>
      Array.isArray(orderableColumns)
        ? [orderableColumns, {}]
        : [Object.keys(orderableColumns) as Array<keyof Row>, orderableColumns],
    [orderableColumns],
  );
  const updateOrder = useCallback(
    (newField: keyof Row) => {
      const newOrder = calculateNewOrder(
        newField,
        order,
        initialOrderForColumn,
      );
      onOrderChange?.(newOrder);
    },
    [initialOrderForColumn, onOrderChange, order],
  );

  const noContent = loading || (!rows.length && emptyMessage);
  const fields = useMemo(() => columns.map(column => column.field), [columns]);

  const selectRow = useStableCallback(
    (row: Row, mode: 'replace' | 'extend' = 'replace') => {
      onSelectRow?.(row);

      // If multi-selection is enabled, and the user shift+clicked the new row,
      // extend the selection from the "anchor" row (first entry in `selectedRows`)
      // to the just-clicked row.
      let newSelection = [row];
      if (mode === 'extend' && selectedRows && selectedRows.length > 0) {
        const startIdx = rows.indexOf(selectedRows[0]);
        const endIdx = rows.indexOf(row);
        if (endIdx >= startIdx) {
          newSelection = rows.slice(startIdx, endIdx + 1);
        } else {
          // We reverse the selection here so that `startIdx` remains the first
          // entry in the list, and is used as the 'anchor' row for future
          // selections.
          newSelection = rows.slice(endIdx, startIdx + 1).reverse();
        }
      }
      onSelectRows?.(newSelection);
    },
  );

  useArrowKeyNavigation(tableRef, {
    selector: 'tbody tr',
    horizontal: true,
    vertical: true,
    focusElement: (element, keyEvent) => {
      // Simulate a click to update the selected row when arrow-key navigation
      // happens. We do this instead of using an `onFocus` handler on the row
      // itself because we need to know if the shift key was pressed, and
      // `FocusEvent` doesn't provide that information.
      if (keyEvent) {
        element.dispatchEvent(
          new MouseEvent('click', {
            // Propagate shift key state so arrow key + shift can be used to
            // create a multi-selection.
            shiftKey: keyEvent.shiftKey,
          }),
        );
      }

      // Scroll selected row into view.
      element.focus();
    },
  });

  const confirmRow = useStableCallback((row: Row) => {
    onConfirmRow?.(row);
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent, row: Row) => {
      // Avoid preventing Enter key interactions in children elements by
      // ignoring events not triggered by the row element itself
      if (event.key === 'Enter' && event.target === event.currentTarget) {
        confirmRow(row);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [confirmRow],
  );

  // Ensure that a selected row is visible when this table is within
  // a scrolling context
  useEffect(() => {
    if (!selectedRow || !scrollContext) {
      return;
    }
    const scrollEl = scrollContext.scrollRef.current;
    const tableHead = tableRef.current?.querySelector('thead');
    const selectedRowEl: HTMLElement | undefined | null =
      tableRef.current?.querySelector('tr[aria-selected="true"]');

    if (scrollEl && selectedRowEl) {
      // Ensure the row is visible within the scroll content area
      const scrollOffset = selectedRowEl.offsetTop - scrollEl.scrollTop;
      if (scrollOffset > scrollEl.clientHeight) {
        selectedRowEl.scrollIntoView();
      }
      // Ensure the row is not obscured by a sticky header
      if (tableHead) {
        const headingHeight = tableHead.clientHeight;
        const headingOffset = scrollOffset - headingHeight;
        if (headingOffset < 0) {
          scrollEl.scrollBy(0, headingOffset);
        }
      }
    }
  }, [selectedRow, tableRef, scrollContext]);

  // Render a <tfoot> element when there are any row data. This absorbs any
  // excess vertical space in tables with sparse rows data.
  const withFoot = !loading && rows.length > 0;

  const selection = useMemo(() => {
    if (selectedRows) {
      return selectedRows;
    } else if (selectedRow) {
      return [selectedRow];
    } else {
      return [];
    }
  }, [selectedRows, selectedRow]);

  const tableRows = useMemo(() => {
    return rows.map((row, idx) => (
      <TableRow
        key={idx}
        selected={selection.includes(row)}
        onClick={e => selectRow(row, e.shiftKey ? 'extend' : 'replace')}
        onDblClick={() => confirmRow(row)}
        onKeyDown={event => handleKeyDown(event, row)}
      >
        {fields.map(field => (
          <TableCell key={field}>{renderItem(row, field)}</TableCell>
        ))}
      </TableRow>
    ));
  }, [
    confirmRow,
    fields,
    renderItem,
    handleKeyDown,
    rows,
    selectRow,
    selection,
  ]);

  const interactive = Boolean(onSelectRow || onSelectRows || onConfirmRow);

  return (
    <Table
      data-composite-component="DataTable"
      role="grid"
      {...htmlAttributes}
      elementRef={downcastRef(tableRef)}
      interactive={interactive}
      stickyHeader
      title={title}
      borderless={borderless}
      striped={striped}
      grid={grid}
    >
      <TableHead>
        <TableRow>
          {columns.map(column => {
            const isOrderable =
              !!onOrderChange && orderableColumnsList.includes(column.field);
            const isActiveOrder = order?.field === column.field;

            return (
              <TableCell
                key={column.field}
                classes={column.classes}
                unpadded={isOrderable}
                aria-sort={isActiveOrder ? order.direction : undefined}
              >
                <HeaderComponent
                  field={column.field.toString()}
                  onClick={
                    isOrderable ? () => updateOrder(column.field) : undefined
                  }
                >
                  <div>{column.label}</div>
                  {isOrderable && (
                    <div
                      className={classnames('rounded p-1', {
                        'bg-white': isActiveOrder,
                      })}
                      aria-hidden
                    >
                      {isActiveOrder &&
                        (order.direction === 'ascending' ? (
                          <ArrowUpIcon />
                        ) : (
                          <ArrowDownIcon />
                        ))}
                      {!isActiveOrder && (
                        <OrderableIcon
                          className={classnames('text-grey-5', {
                            // Interactive rows set a darker background color on
                            // hover.
                            // Setting a darker color on the icon when hovering
                            // the row will ensure enough contrast.
                            'group-hover:text-grey-7': interactive,
                          })}
                        />
                      )}
                    </div>
                  )}
                </HeaderComponent>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {!loading && tableRows}
        {noContent && (
          <TableRow>
            <TableCell colSpan={columns.length} classes="text-center p-3">
              {loading ? (
                <SpinnerSpokesIcon className="inline w-2em h-2em" />
              ) : (
                <>{emptyMessage}</>
              )}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {children}
      {withFoot && <TableFoot />}
    </Table>
  );
}
